// frontend/src/pages/Landing.js
import React, { useEffect, useRef, useState } from 'react';
import { Link, useSwitchLanguage } from '../routing';
import { Sparkles, Clock, Shield, RotateCcw, Star, ChevronDown, Menu, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { motion, useScroll, useTransform, useReducedMotion } from 'framer-motion';
import { rememberLocale } from '../utils/locale';
import { LANGUAGES, nextLanguage } from '../i18n';
import { API_BASE, PLACEHOLDER_IMG } from '../config';
import TraderIdentity from '../components/TraderIdentity';
import HairFlow3D from '../components/HairFlow3D';
import { Reveal, Tilt3D, WordsIn } from '../components/Motion';
import theme from '../theme';

const navLinkStyle = { color: theme.alpha.white(0.75), textDecoration: 'none', fontSize: 11, letterSpacing: '0.15em', textTransform: 'uppercase' };
const navCtaStyle = { background: 'transparent', border: `1px solid ${theme.alpha.white(0.45)}`, color: theme.white, padding: '9px 22px', borderRadius: 2, textDecoration: 'none', fontSize: 11, letterSpacing: '0.12em', textTransform: 'uppercase' };
const mobileLinkStyle = { color: theme.alpha.white(0.85), textDecoration: 'none', fontSize: 13, letterSpacing: '0.12em', padding: '13px 0', borderBottom: `1px solid ${theme.alpha.white(0.1)}`, textTransform: 'uppercase' };

const MARQUEE_ITEMS_FALLBACK = [
  'LUXURY HAIR RENTAL', 'REMY QUALITY', 'SMART LOCKER', 'CYPRUS',
  '24/7 PICKUP', 'ZERO COMMITMENT', 'REFUNDABLE DEPOSIT', 'PROFESSIONAL CLEANING',
];

// The collection preview and the Instagram strip used to be six stock photos
// from Unsplash and three invented products ("Honey Blonde Goddess", €25/day,
// one of them badged BEST SELLER) — none of which we own, stock, or have ever
// sold. Advertising a product with a picture of a different product, and a
// best-seller badge on something never sold, are misleading actions under the
// UCPD; the stock licence also does not carry model releases for using a
// person's face to sell a service. The preview now shows real rows from the
// catalogue and renders nothing when the catalogue is empty.

const IgIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
    <rect x="2" y="2" width="20" height="20" rx="5"/>
    <circle cx="12" cy="12" r="5"/>
    <circle cx="17.5" cy="6.5" r="1" fill="currentColor" stroke="none"/>
  </svg>
);
const TkIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor">
    <path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-2.88 2.5 2.89 2.89 0 01-2.89-2.89 2.89 2.89 0 012.89-2.89c.28 0 .54.04.79.1V9.01a6.32 6.32 0 00-.79-.05A6.34 6.34 0 003.15 15.3a6.34 6.34 0 006.34 6.34 6.34 6.34 0 006.34-6.34V9.56a8.16 8.16 0 004.77 1.52V7.61a4.85 4.85 0 01-1.01-.92z"/>
  </svg>
);

export default function Landing() {
  const { t, i18n } = useTranslation();
  const marqueeItems = t('landing.marqueeItems', { returnObjects: true }) || MARQUEE_ITEMS_FALLBACK;
  const [liveReviews, setLiveReviews] = useState({ reviews: [], average: null, count: 0 });
  const [previewProducts, setPreviewProducts] = useState([]);
  const [nlEmail, setNlEmail] = useState('');
  const [nlState, setNlState] = useState('idle'); // idle | loading | success | error
  const [menuOpen, setMenuOpen] = useState(false);

  // Hero parallax: the copy drifts up and dissolves as the 3D field behind it
  // stays put, which is what sells the depth. Scroll-linked rather than
  // autoplaying, but still suppressed under prefers-reduced-motion — parallax
  // is one of the effects that actually triggers motion sickness.
  const heroRef = useRef(null);
  const reducedMotion = useReducedMotion();
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ['start start', 'end start'] });
  const heroY = useTransform(scrollYProgress, [0, 1], [0, 110]);
  const heroFade = useTransform(scrollYProgress, [0, 0.75], [1, 0]);
  const heroStyle = reducedMotion ? undefined : { y: heroY, opacity: heroFade };

  // The bar is fixed, transparent and sets white text, so from the first white
  // section down it was white-on-white — the logo and every link simply
  // vanished. It now takes on the hero's black once the hero is behind you.
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const onScroll = () => setScrolled((window.scrollY || 0) > 80);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    const style = document.createElement('style');
    style.id = 'gg-marquee-style';
    style.textContent = `
      @keyframes gg-marquee { from { transform: translateX(0) } to { transform: translateX(-50%) } }
      .gg-marquee { animation: gg-marquee 35s linear infinite; display: flex; width: max-content; }
      .gg-marquee:hover { animation-play-state: paused; }
    `;
    document.head.appendChild(style);

    return () => {
      const s = document.getElementById('gg-marquee-style');
      if (s) document.head.removeChild(s);
    };
  }, []);

  // The endpoint returns the quotes AND the aggregate over every non-hidden
  // review, because the two have to be rendered together — see the section
  // below. Older builds returned a bare array; tolerate both so a cached bundle
  // against a new backend, or the reverse, degrades to "no reviews" rather than
  // throwing on .reviews of undefined.
  useEffect(() => {
    fetch(`${API_BASE}/reviews/top`)
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (Array.isArray(data)) { setLiveReviews({ reviews: data, average: null, count: data.length }); return; }
        if (data && Array.isArray(data.reviews)) setLiveReviews(data);
      })
      .catch(() => {});
  }, []);

  // Real catalogue rows for the preview below. A failure leaves the list empty,
  // and an empty list hides the section — the page degrades to "no collection
  // shown", never to "here are three products we invented".
  useEffect(() => {
    fetch(`${API_BASE}/products?limit=3`)
      .then(r => r.ok ? r.json() : [])
      .then(data => { if (Array.isArray(data)) setPreviewProducts(data.slice(0, 3)); })
      .catch(() => {});
  }, []);

  // Navigates rather than only swapping the strings: with one URL per language
  // (/el/…, /en/…) leaving the path untouched would put English text under a
  // URL that still said /el — wrong for the reader who shares the link, and a
  // contradiction for the crawler that was told those are two pages.
  const switchLanguage = useSwitchLanguage();

  const toggleLang = () => {
    const next = nextLanguage(i18n.language);
    switchLanguage(next);
    rememberLocale(next);
  };

  const faqs = t('landing.faqs', { returnObjects: true });
  const steps = t('landing.steps', { returnObjects: true });
  // Only real reviews are ever shown. There used to be a set of invented ones —
  // invented quotes, invented first names, invented towns — displayed under the
  // heading "Real Reviews" whenever fewer than three genuine reviews existed,
  // which on a new business meant always. Presenting fabricated reviews as
  // genuine is on the UCPD Annex I blacklist (¶23b/23c): unfair per se, no
  // balancing test available. An empty section is the honest state, so when
  // there is nothing real to show the section does not render at all.
  //
  // The three shown are the three most recent, whatever they say. They were the
  // three highest-rated until 2026-08-10; see routes/reviews.js for why that was
  // the same blacklist entry wearing a different hat.
  const testimonials = liveReviews.reviews.length >= 3
    ? liveReviews.reviews.slice(0, 3).map(r => ({ name: r.full_name || t('landing.anonymousReviewer'), text: r.comment, rating: r.rating, location: t('landing.reviewerLocation') }))
    : [];


  const handleNewsletter = async (e) => {
    e.preventDefault();
    if (!nlEmail || nlState === 'loading') return;
    setNlState('loading');
    try {
      const res = await fetch(`${API_BASE}/newsletter/subscribe`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: nlEmail }),
      });
      setNlState(res.ok ? 'success' : 'error');
    } catch {
      setNlState('error');
    }
  };

  return (
    <div style={{ fontFamily: theme.font.body, background: theme.cream, overflowX: 'hidden' }}>

      {/* NAVBAR */}
      {/* safe-top/x: pinned to the edges, so on Android 15 edge-to-edge this
          would otherwise sit under the status bar and the gesture insets. */}
      {/* Five items at gap 24 with no wrap need ~590px in Greek, and the page
          wrapper sets overflowX:'hidden' — on a 380px phone (i.e. the Android
          app's first screen) Sign In was pushed off-screen and unreachable.
          Same hamburger pattern as components/Navbar.js. */}
      <nav className="lp-nav" style={{
        position: 'fixed', top: 0, left: 0, right: 0, zIndex: 100,
        background: menuOpen ? theme.alpha.inkDeep(0.97) : scrolled ? theme.alpha.inkDeep(0.82) : 'transparent',
        backdropFilter: scrolled && !menuOpen ? 'blur(16px)' : 'none',
        borderBottom: scrolled && !menuOpen ? `1px solid ${theme.alpha.gold(0.16)}` : '1px solid transparent',
        transition: 'background 0.35s ease, border-color 0.35s ease',
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Sparkles size={15} color={theme.gold} />
            <span style={{ fontFamily: theme.font.display, fontSize: 26, fontWeight: 600, color: theme.white, letterSpacing: '0.08em' }}>
              GLAM<span style={{ color: theme.gold }}>GO</span>
            </span>
          </div>

          {/* Desktop */}
          <div className="lp-nav-links" style={{ display: 'flex', gap: 24, alignItems: 'center' }}>
            {/* textTransform instead of .toUpperCase(): JS uppercasing keeps the
                Greek tonos (ΠΕΡΙΗΓΉΣΗ), which is a spelling error — browsers drop
                it correctly for the CSS transform. */}
            <Link to="/how-it-works" style={navLinkStyle}>{t('nav.howItWorks')}</Link>
            <Link to="/browse" style={navLinkStyle}>{t('nav.browse')}</Link>
            <button onClick={toggleLang} aria-label={t('nav.switchLanguage')} style={{ background: 'transparent', border: `1px solid ${theme.alpha.white(0.3)}`, color: theme.alpha.white(0.7), padding: '5px 12px', borderRadius: 2, cursor: 'pointer', fontSize: 10, letterSpacing: '0.12em', fontFamily: theme.font.body }}>
              {LANGUAGES[nextLanguage(i18n.language)]?.short ?? 'EN'}
            </button>
            <Link to="/login" style={navCtaStyle}>{t('nav.signIn')}</Link>
          </div>

          {/* Mobile hamburger */}
          <button
            className="lp-nav-burger"
            onClick={() => setMenuOpen(o => !o)}
            aria-label={t('nav.menu')}
            aria-expanded={menuOpen}
            style={{ display: 'none', background: 'none', border: 'none', color: theme.white, cursor: 'pointer', padding: 6, lineHeight: 0 }}>
            {menuOpen ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>

        {menuOpen && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4, paddingTop: 18 }}>
            <Link to="/how-it-works" onClick={() => setMenuOpen(false)} style={mobileLinkStyle}>{t('nav.howItWorks')}</Link>
            <Link to="/browse" onClick={() => setMenuOpen(false)} style={mobileLinkStyle}>{t('nav.browse')}</Link>
            <Link to="/login" onClick={() => setMenuOpen(false)} style={{ ...navCtaStyle, display: 'block', textAlign: 'center', marginTop: 12, padding: '13px 22px' }}>{t('nav.signIn')}</Link>
            <button onClick={toggleLang} aria-label={t('nav.switchLanguage')} style={{ marginTop: 10, width: '100%', padding: '12px', background: 'transparent', border: `1px solid ${theme.alpha.white(0.3)}`, color: theme.alpha.white(0.7), borderRadius: 2, cursor: 'pointer', fontSize: 11, letterSpacing: '0.12em', fontFamily: theme.font.body }}>
              {LANGUAGES[nextLanguage(i18n.language)]?.short ?? 'EN'}
            </button>
          </div>
        )}

        <style>{`
          /* The safe-top/safe-x helper classes set padding of their own, and any
             padding shorthand declared later simply replaces them — which the old
             inline padding:'20px 40px' already did, silently dropping the Android
             15 edge-to-edge insets this bar is pinned into. Fold the insets into
             the values instead of layering two rules that fight. */
          .lp-nav {
            padding-top: calc(20px + env(safe-area-inset-top, 0px));
            padding-bottom: 20px;
            padding-left: calc(40px + env(safe-area-inset-left, 0px));
            padding-right: calc(40px + env(safe-area-inset-right, 0px));
          }
          @media (max-width: 768px) {
            .lp-nav {
              padding-top: calc(16px + env(safe-area-inset-top, 0px));
              padding-bottom: 16px;
              padding-left: calc(20px + env(safe-area-inset-left, 0px));
              padding-right: calc(20px + env(safe-area-inset-right, 0px));
            }
            .lp-nav-links { display: none !important; }
            .lp-nav-burger { display: block !important; }
          }
        `}</style>
      </nav>

      {/* HERO */}
      <section ref={heroRef} style={{ minHeight: '100vh', background: theme.inkDeep, display: 'flex', alignItems: 'center', justifyContent: 'center', textAlign: 'center', padding: '120px 24px 80px', position: 'relative', overflow: 'hidden' }}>
        {/* The 3D strand field. Its children are the fallback: the original
            decorative orbs, drawn only if WebGL is unavailable or the context
            is lost, so the hero degrades to exactly what it used to be. */}
        <HairFlow3D>
          <div style={{ position: 'absolute', width: 700, height: 700, borderRadius: '50%', background: `radial-gradient(circle, ${theme.alpha.gold(0.08)} 0%, transparent 65%)`, top: '50%', left: '50%', transform: 'translate(-50%,-50%)', pointerEvents: 'none' }} />
          <div style={{ position: 'absolute', width: 350, height: 350, borderRadius: '50%', background: `radial-gradient(circle, ${theme.alpha.gold(0.05)} 0%, transparent 70%)`, top: '12%', right: '6%', pointerEvents: 'none' }} />
          <div style={{ position: 'absolute', width: 200, height: 200, borderRadius: '50%', background: `radial-gradient(circle, ${theme.alpha.gold(0.06)} 0%, transparent 70%)`, bottom: '18%', left: '4%', pointerEvents: 'none' }} />
        </HairFlow3D>
        {/* Grid overlay */}
        <div style={{ position: 'absolute', inset: 0, backgroundImage: `linear-gradient(${theme.alpha.white(0.015)} 1px, transparent 1px), linear-gradient(90deg, ${theme.alpha.white(0.015)} 1px, transparent 1px)`, backgroundSize: '80px 80px', pointerEvents: 'none' }} />

        <motion.div style={{ position: 'relative', maxWidth: 860, ...heroStyle }}>
          <motion.div
            initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 10, background: theme.alpha.gold(0.1), border: `1px solid ${theme.alpha.gold(0.22)}`, padding: '8px 22px', borderRadius: 100, marginBottom: 40 }}>
            <Sparkles size={11} color={theme.gold} />
            <span style={{ color: theme.gold, fontSize: 10, letterSpacing: '0.28em', fontFamily: theme.font.body }}>{t('landing.badge')}</span>
          </motion.div>

          {/* The two title lines rise word by word out of the plane, so the
              headline arrives on the same axis as the strands behind it. */}
          <h1 style={{ fontFamily: theme.font.display, fontSize: 'clamp(58px, 11vw, 108px)', fontWeight: 300, color: theme.white, lineHeight: 1.0, margin: '0 0 20px', letterSpacing: '-0.02em' }}>
            <WordsIn text={t('landing.heroTitle1')} delay={0.3} /><br />
            <WordsIn text={t('landing.heroTitle2')} delay={0.52} style={{ color: theme.gold, fontStyle: 'italic' }} />
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.95, ease: [0.22, 1, 0.36, 1] }}
            style={{ color: theme.alpha.white(0.5), fontSize: 16, lineHeight: 1.85, maxWidth: 500, margin: '0 auto 52px', fontWeight: 300, letterSpacing: '0.02em' }}>
            {t('landing.heroSubtitle')}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1.1, ease: [0.22, 1, 0.36, 1] }}
            style={{ display: 'flex', gap: 14, justifyContent: 'center', flexWrap: 'wrap' }}>
            <Link to="/browse" style={{ background: `linear-gradient(135deg, ${theme.gold}, ${theme.goldDeep})`, color: theme.white, padding: '17px 52px', borderRadius: 2, textDecoration: 'none', fontSize: 11, letterSpacing: '0.18em', fontFamily: theme.font.body, fontWeight: 500, boxShadow: `0 8px 40px ${theme.alpha.gold(0.25)}` }}>
              {t('landing.browseCollection')}
            </Link>
            <Link to="/how-it-works" style={{ background: 'transparent', border: `1px solid ${theme.alpha.white(0.22)}`, color: theme.alpha.white(0.8), padding: '17px 52px', borderRadius: 2, textDecoration: 'none', fontSize: 11, letterSpacing: '0.18em', fontFamily: theme.font.body }}>
              {t('landing.howItWorks')}
            </Link>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1.28, ease: [0.22, 1, 0.36, 1] }}
            style={{ display: 'flex', gap: 56, justifyContent: 'center', marginTop: 72, borderTop: `1px solid ${theme.alpha.white(0.07)}`, paddingTop: 40 }}>
            {/* Facts about the service, not counts of customers we do not have
                yet. The two figures that used to sit here — "300+ rentals this
                season" and "4.9★ average rating" — were hardcoded against a
                database holding zero rentals and zero reviews. Inventing social
                proof is a blacklisted commercial practice (UCPD Annex I ¶23b),
                not merely optimistic copy, so nothing here may be a number we
                cannot point at a row for. */}
            {[
              ['24/7', t('landing.smartLocker')],
              ['48h', t('landing.statDeposit')],
              ['3–7', t('landing.dayRentals')],
            ].map(([val, label]) => (
              <div key={val} style={{ textAlign: 'center' }}>
                <div style={{ fontFamily: theme.font.display, fontSize: 36, color: theme.gold, fontWeight: 400 }}>{val}</div>
                {/* CSS uppercase, not JS: 'ενοικιάσεις'.toUpperCase() keeps the
                    tonos, which Greek orthography drops in all-caps. */}
                <div style={{ color: theme.alpha.white(0.35), fontSize: 10, letterSpacing: '0.12em', marginTop: 8, textTransform: 'uppercase' }}>{label}</div>
              </div>
            ))}
          </motion.div>
        </motion.div>
      </section>

      {/* MARQUEE STRIP */}
      <div style={{ background: theme.gold, padding: '13px 0', overflow: 'hidden' }}>
        <div className="gg-marquee">
          {[...marqueeItems, ...marqueeItems].map((item, i) => (
            <span key={i} style={{ display: 'inline-flex', alignItems: 'center', gap: 18, padding: '0 22px', fontSize: 10, letterSpacing: '0.22em', fontWeight: 500, color: theme.white, whiteSpace: 'nowrap' }}>
              {item} <span style={{ opacity: 0.45 }}>✦</span>
            </span>
          ))}
        </div>
      </div>

      {/* HOW IT WORKS */}
      <section style={{ padding: '100px 24px', background: theme.white }}>
        <div style={{ maxWidth: 1100, margin: '0 auto' }}>
          <Reveal style={{ textAlign: 'center', marginBottom: 72 }}>
            <p style={{ color: theme.gold, fontSize: 10, letterSpacing: '0.25em', marginBottom: 16 }}>{t('landing.processLabel')}</p>
            <h2 style={{ fontFamily: theme.font.display, fontSize: 'clamp(36px, 5vw, 56px)', fontWeight: 400, color: theme.ink, margin: 0 }}>{t('landing.processTitle')}</h2>
          </Reveal>
          {/* perspective on the grid, not the cards: shared vanishing point, so
              the three steps tip up as one surface rather than three. */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(220px, 100%), 1fr))', gap: 48, perspective: 1100 }}>
            {steps.map((step, i) => (
              <Reveal key={i} delay={i * 0.12} y={24} rotate={18} style={{ textAlign: 'center' }}>
                <div style={{ width: 56, height: 56, borderRadius: '50%', background: theme.alpha.gold(0.08), border: `1px solid ${theme.alpha.gold(0.22)}`, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 22px' }}>
                  <span style={{ fontFamily: theme.font.display, fontSize: 22, color: theme.gold, fontWeight: 500 }}>0{i + 1}</span>
                </div>
                <h3 style={{ fontFamily: theme.font.display, fontSize: 22, color: theme.ink, margin: '0 0 12px', fontWeight: 500 }}>{step.title}</h3>
                <p style={{ color: theme.grey550, fontSize: 13, lineHeight: 1.75, margin: 0 }}>{step.desc}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* COLLECTION PREVIEW — real catalogue rows, or nothing at all. */}
      {previewProducts.length > 0 && (
      <section style={{ padding: '100px 24px', background: theme.cream }}>
        <div style={{ maxWidth: 1100, margin: '0 auto' }}>
          <Reveal style={{ textAlign: 'center', marginBottom: 56 }}>
            <p style={{ color: theme.gold, fontSize: 10, letterSpacing: '0.25em', marginBottom: 16 }}>{t('landing.collectionLabel')}</p>
            <h2 style={{ fontFamily: theme.font.display, fontSize: 'clamp(36px, 5vw, 56px)', fontWeight: 400, color: theme.ink, margin: 0 }}>{t('landing.collectionTitle')}</h2>
          </Reveal>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(280px, 100%), 1fr))', gap: 24 }}>
            {previewProducts.map((item, i) => (
              <Reveal key={item.id} delay={i * 0.1}>
              <Tilt3D max={8} style={{ height: '100%' }}>
              <div style={{ borderRadius: 4, overflow: 'hidden', background: theme.white, boxShadow: `0 2px 20px ${theme.alpha.black(0.06)}`, height: '100%' }}>
                <div style={{ height: 340, overflow: 'hidden', position: 'relative' }}>
                  <img src={item.image_urls?.[0] || PLACEHOLDER_IMG} alt={item.name}
                    style={{ width: '100%', height: '100%', objectFit: 'cover', transition: 'transform 0.5s ease' }}
                    onMouseEnter={e => { e.target.style.transform = 'scale(1.06)'; }}
                    onMouseLeave={e => { e.target.style.transform = 'scale(1)'; }}
                  />
                  <div style={{ position: 'absolute', top: 16, right: 16, background: theme.inkDeep, color: theme.gold, padding: '5px 14px', borderRadius: 100, fontSize: 11, fontWeight: 500 }}>
                    {t('landing.pricePerDay', { price: parseFloat(item.base_price_per_day).toFixed(0) })}
                  </div>
                </div>
                <div style={{ padding: '20px 24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <h3 style={{ fontFamily: theme.font.display, fontSize: 20, color: theme.ink, margin: '0 0 4px' }}>{item.name}</h3>
                    <p style={{ color: theme.grey400, fontSize: 12, margin: 0, letterSpacing: '0.04em' }}>{item.color}</p>
                  </div>
                  <Link to={`/product/${item.id}`} style={{ color: theme.gold, fontSize: 11, textDecoration: 'none', letterSpacing: '0.1em', borderBottom: `1px solid ${theme.gold}`, paddingBottom: 2 }}>
                    {t('landing.rentBtn')}
                  </Link>
                </div>
              </div>
              </Tilt3D>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.15} style={{ textAlign: 'center', marginTop: 48 }}>
            <Link to="/browse" style={{ display: 'inline-block', border: `1px solid ${theme.ink}`, color: theme.ink, padding: '15px 52px', textDecoration: 'none', fontSize: 11, letterSpacing: '0.18em', borderRadius: 2 }}>
              {t('landing.viewAll')}
            </Link>
          </Reveal>
        </div>
      </section>
      )}

      {/* STATS / NUMBERS */}
      <section style={{ background: theme.inkDeep, padding: '80px 24px' }}>
        <div style={{ maxWidth: 900, margin: '0 auto', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(160px, 100%), 1fr))', gap: 48, textAlign: 'center', perspective: 1100 }}>
          {[
            { val: '24/7', label: t('landing.smartLocker') },
            { val: '48h', label: t('landing.statDeposit') },
            { val: '€0', label: t('landing.statFees') },
            { val: '100%', label: t('landing.statRemy') },
          ].map((s, i) => (
            <Reveal key={s.val} delay={i * 0.1} y={22} rotate={20}>
              <div style={{ fontFamily: theme.font.display, fontSize: 54, color: theme.gold, fontWeight: 300, lineHeight: 1 }}>{s.val}</div>
              <div style={{ color: theme.alpha.white(0.4), fontSize: 10, letterSpacing: '0.15em', marginTop: 12, textTransform: 'uppercase' }}>{s.label}</div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* FOLLOW THE GLOW — Instagram / TikTok */}
      <section style={{ padding: '100px 24px', background: theme.creamDeep, textAlign: 'center' }}>
        <div style={{ maxWidth: 1100, margin: '0 auto' }}>
          <Reveal>
            <p style={{ color: theme.gold, fontSize: 10, letterSpacing: '0.25em', marginBottom: 16 }}>{t('landing.socialLabel')}</p>
            <h2 style={{ fontFamily: theme.font.display, fontSize: 'clamp(32px, 5vw, 52px)', fontWeight: 400, color: theme.ink, margin: '0 0 12px' }}>{t('landing.socialTitle')}</h2>
            <p style={{ color: theme.grey550, fontSize: 14, maxWidth: 420, margin: '0 auto 48px', lineHeight: 1.75 }}>{t('landing.socialSubtitle')}</p>
          </Reveal>

          {/* The six-tile grid that used to sit here was presented as our
              Instagram feed and was six unrelated stock portraits. Restore it
              when there are real posts to show — pulling them from the actual
              account, not from a stock library. */}

          <Reveal delay={0.12} style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap' }}>
            <a href="https://instagram.com/glamgo.hair" target="_blank" rel="noreferrer"
              style={{ background: theme.inkDeep, color: theme.white, padding: '13px 32px', textDecoration: 'none', fontSize: 11, letterSpacing: '0.15em', borderRadius: 2, display: 'inline-flex', alignItems: 'center', gap: 10 }}>
              <IgIcon /> {t('landing.followInstagram')}
            </a>
            <a href="https://tiktok.com/@glamgo.hair" target="_blank" rel="noreferrer"
              style={{ background: 'transparent', border: `1px solid ${theme.ink}`, color: theme.ink, padding: '13px 32px', textDecoration: 'none', fontSize: 11, letterSpacing: '0.15em', borderRadius: 2, display: 'inline-flex', alignItems: 'center', gap: 10 }}>
              <TkIcon /> {t('landing.followTiktok')}
            </a>
          </Reveal>
        </div>
      </section>

      {/* TESTIMONIALS — rendered only when real reviews exist (see above). */}
      {testimonials.length > 0 && (
      <section style={{ padding: '100px 24px', background: theme.inkDeep }}>
        <div style={{ maxWidth: 1100, margin: '0 auto' }}>
          <Reveal style={{ textAlign: 'center', marginBottom: 64 }}>
            <p style={{ color: theme.gold, fontSize: 10, letterSpacing: '0.25em', marginBottom: 16 }}>{t('landing.testimonialsLabel')}</p>
            <h2 style={{ fontFamily: theme.font.display, fontSize: 'clamp(36px, 5vw, 56px)', fontWeight: 400, color: theme.white, margin: 0 }}>{t('landing.testimonialsTitle')}</h2>
            {/* The aggregate, over every review we hold, next to a section that
                can only fit three of them. Without it three quotes ARE the
                rating as far as the reader is concerned, and three is a number
                we choose. */}
            {liveReviews.average !== null && (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10, marginTop: 20 }}>
                <div style={{ display: 'flex', gap: 3 }}>
                  {[1, 2, 3, 4, 5].map(n => (
                    <Star key={n} size={15}
                      fill={n <= Math.round(liveReviews.average) ? theme.gold : 'none'}
                      color={n <= Math.round(liveReviews.average) ? theme.gold : theme.alpha.white(0.28)} />
                  ))}
                </div>
                <span style={{ fontFamily: theme.font.display, fontSize: 20, color: theme.white }}>{liveReviews.average.toFixed(1)}</span>
                <span style={{ color: theme.alpha.white(0.4), fontSize: 13 }}>
                  {t('landing.reviewsAverage', { count: liveReviews.count })}
                </span>
              </div>
            )}
          </Reveal>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(280px, 100%), 1fr))', gap: 24 }}>
            {testimonials.map((item, i) => (
              <Reveal key={item.name} delay={i * 0.1} style={{ height: '100%' }}>
              <Tilt3D max={6}>
              <div style={{ background: theme.alpha.white(0.04), border: `1px solid ${theme.alpha.gold(0.14)}`, borderRadius: 4, padding: 36, height: '100%' }}>
                {/* All five stars, filled or not. Rendering only the earned
                    ones made a two-star review look like a small compliment
                    rather than a complaint. */}
                <div style={{ display: 'flex', gap: 4, marginBottom: 20 }}>
                  {[1, 2, 3, 4, 5].map(n => (
                    <Star key={n} size={13}
                      fill={n <= item.rating ? theme.gold : 'none'}
                      color={n <= item.rating ? theme.gold : theme.alpha.white(0.25)} />
                  ))}
                </div>
                <p style={{ color: theme.alpha.white(0.72), fontSize: 15, lineHeight: 1.8, margin: '0 0 24px', fontStyle: 'italic', fontFamily: theme.font.display }}>"{item.text}"</p>
                <div>
                  <div style={{ color: theme.gold, fontWeight: 500, fontSize: 13, letterSpacing: '0.05em' }}>{item.name}</div>
                  <div style={{ color: theme.alpha.white(0.3), fontSize: 12, marginTop: 2 }}>{item.location}</div>
                </div>
              </div>
              </Tilt3D>
              </Reveal>
            ))}
          </div>
          {/* Art. 6a of the Consumer Rights Directive: a trader who shows
              consumer reviews must state whether and how it checks they come
              from people who actually used the service. We do check — a review
              can only be posted against a rental in state 'returned' — but the
              check was invisible, and an undisclosed safeguard does not satisfy
              the article. */}
          <p style={{ color: theme.alpha.white(0.35), fontSize: 12, textAlign: 'center', marginTop: 40, lineHeight: 1.7 }}>
            {t('landing.reviewsVerified')}
          </p>
        </div>
      </section>
      )}

      {/* NEWSLETTER */}
      <section style={{ padding: '100px 24px', background: theme.cream, textAlign: 'center' }}>
        <Reveal style={{ maxWidth: 560, margin: '0 auto' }}>
          <p style={{ color: theme.gold, fontSize: 10, letterSpacing: '0.25em', marginBottom: 16 }}>{t('landing.newsletterLabel')}</p>
          <h2 style={{ fontFamily: theme.font.display, fontSize: 'clamp(32px, 5vw, 52px)', fontWeight: 400, color: theme.ink, margin: '0 0 16px' }}>{t('landing.newsletterTitle')}</h2>
          <p style={{ color: theme.grey550, fontSize: 14, lineHeight: 1.75, marginBottom: 16 }}>{t('landing.newsletterSubtitle')}</p>
          {/* An advertised "€10 off" with no conditions attached is a misleading
              omission — the consumer cannot tell what they are being offered. */}
          <p style={{ color: theme.grey400, fontSize: 11.5, lineHeight: 1.7, marginBottom: 32, maxWidth: 440, marginLeft: 'auto', marginRight: 'auto' }}>
            {t('landing.newsletterTerms')}
          </p>

          {nlState === 'success' ? (
            <div style={{ background: theme.alpha.gold(0.1), border: `1px solid ${theme.alpha.gold(0.28)}`, borderRadius: 4, padding: '22px 32px', color: theme.ink, fontSize: 16, fontFamily: theme.font.display }}>
              ✦ {t('landing.newsletterSuccess')}
            </div>
          ) : (
            <form onSubmit={handleNewsletter} style={{ display: 'flex', maxWidth: 480, margin: '0 auto 14px' }}>
              <input
                type="email" required value={nlEmail}
                onChange={e => setNlEmail(e.target.value)}
                placeholder={t('landing.newsletterPlaceholder')}
                style={{ flex: 1, padding: '15px 20px', border: `1px solid ${theme.grey120}`, borderRight: 'none', borderRadius: '2px 0 0 2px', fontSize: 13, fontFamily: theme.font.body, background: theme.white, outline: 'none', color: theme.ink }}
              />
              <button type="submit" disabled={nlState === 'loading'} style={{ background: theme.inkDeep, color: theme.white, padding: '15px 28px', border: 'none', borderRadius: '0 2px 2px 0', cursor: 'pointer', fontSize: 10, letterSpacing: '0.18em', fontFamily: theme.font.body, fontWeight: 500, whiteSpace: 'nowrap' }}>
                {nlState === 'loading' ? '...' : t('landing.newsletterCta')}
              </button>
            </form>
          )}
          {nlState === 'error' && (
            <p style={{ color: theme.redBrick, fontSize: 12, marginTop: 8 }}>{t('landing.newsletterError')}</p>
          )}
          <p style={{ color: theme.slate400, fontSize: 11, letterSpacing: '0.06em', marginTop: 16 }}>{t('landing.newsletterDisclaimer')}</p>
        </Reveal>
      </section>

      {/* FAQ */}
      <section style={{ padding: '100px 24px', background: theme.white }}>
        <div style={{ maxWidth: 800, margin: '0 auto' }}>
          <Reveal style={{ textAlign: 'center', marginBottom: 64 }}>
            <p style={{ color: theme.gold, fontSize: 10, letterSpacing: '0.25em', marginBottom: 16 }}>{t('landing.faqLabel')}</p>
            <h2 style={{ fontFamily: theme.font.display, fontSize: 'clamp(36px, 5vw, 56px)', fontWeight: 400, color: theme.ink, margin: 0 }}>{t('landing.faqTitle')}</h2>
          </Reveal>
          {faqs.map((faq, i) => (
            <Reveal key={i} delay={Math.min(i, 5) * 0.06} y={18}>
              <FaqItem q={faq.q} a={faq.a} />
            </Reveal>
          ))}
        </div>
      </section>

      {/* TRUST BADGES */}
      <section style={{ padding: '60px 24px', background: theme.cream, borderTop: `1px solid ${theme.sand}` }}>
        <div style={{ maxWidth: 900, margin: '0 auto', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(180px, 100%), 1fr))', gap: 40, textAlign: 'center' }}>
          {[
            { icon: <Shield size={26} color={theme.gold} />, title: t('landing.securePayments'), sub: t('landing.poweredByStripe') },
            { icon: <Clock size={26} color={theme.gold} />, title: t('landing.access247'), sub: t('landing.smartLockerCollection') },
            { icon: <RotateCcw size={26} color={theme.gold} />, title: t('landing.easyReturns'), sub: t('landing.depositBack48h') },
            { icon: <Sparkles size={26} color={theme.gold} />, title: t('landing.remyQuality'), sub: t('landing.proClean') },
          ].map((b, i) => (
            <Reveal key={b.title} delay={i * 0.08} y={20}>
              <div style={{ marginBottom: 14 }}>{b.icon}</div>
              <div style={{ fontFamily: theme.font.display, fontSize: 17, color: theme.ink, fontWeight: 500 }}>{b.title}</div>
              <div style={{ color: theme.grey550, fontSize: 12, marginTop: 4 }}>{b.sub}</div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* CTA BANNER */}
      <section style={{ padding: '110px 24px', background: theme.inkDeep, textAlign: 'center', position: 'relative', overflow: 'hidden' }}>
        {/* A slow breath of gold behind the closing line — the only ambient
            loop on the page outside the hero, and it stops for anyone who has
            asked for reduced motion. */}
        <motion.div
          aria-hidden="true"
          style={{ position: 'absolute', width: 900, height: 900, left: '50%', top: '50%', marginLeft: -450, marginTop: -450, borderRadius: '50%', background: `radial-gradient(circle, ${theme.alpha.gold(0.14)} 0%, transparent 62%)`, pointerEvents: 'none' }}
          animate={reducedMotion ? undefined : { scale: [1, 1.12, 1], opacity: [0.75, 1, 0.75] }}
          transition={{ duration: 9, repeat: Infinity, ease: 'easeInOut' }}
        />
        <Reveal style={{ position: 'relative' }}>
          <p style={{ color: theme.gold, fontSize: 10, letterSpacing: '0.3em', marginBottom: 28 }}>GLAMGO</p>
          <h2 style={{ fontFamily: theme.font.display, fontSize: 'clamp(40px, 7vw, 76px)', fontWeight: 300, color: theme.white, margin: '0 0 20px', lineHeight: 1.1 }}>
            {t('landing.ctaTitle')}
          </h2>
          <p style={{ color: theme.alpha.white(0.45), fontSize: 16, maxWidth: 440, margin: '0 auto 52px', lineHeight: 1.75 }}>
            {t('landing.ctaSubtitle')}
          </p>
          <Link to="/browse" style={{ display: 'inline-block', background: `linear-gradient(135deg, ${theme.gold}, ${theme.goldDeep})`, color: theme.white, padding: '18px 60px', borderRadius: 2, textDecoration: 'none', fontSize: 11, letterSpacing: '0.2em', fontWeight: 500, boxShadow: `0 8px 40px ${theme.alpha.gold(0.2)}` }}>
            {t('landing.startBrowsing')}
          </Link>
        </Reveal>
      </section>

      {/* FOOTER */}
      <footer style={{ background: theme.inkDeep, borderTop: `1px solid ${theme.alpha.white(0.05)}`, padding: '60px 24px 32px', color: theme.alpha.white(0.4), fontSize: 13 }}>
        <div style={{ maxWidth: 1100, margin: '0 auto' }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(200px, 100%), 1fr))', gap: 48, marginBottom: 48 }}>
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
                <Sparkles size={13} color={theme.gold} />
                <span style={{ fontFamily: theme.font.display, fontSize: 22, color: theme.gold, letterSpacing: '0.08em' }}>GLAMGO</span>
              </div>
              <div style={{ lineHeight: 1.75, maxWidth: 220, fontSize: 12 }}>{t('landing.footerTagline')}</div>
            </div>
            <div>
              <div style={{ color: theme.white, fontSize: 10, letterSpacing: '0.18em', marginBottom: 20 }}>{t('landing.footerLinks')}</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                <Link to="/browse" style={{ color: theme.alpha.white(0.4), textDecoration: 'none', fontSize: 13 }}>{t('nav.browse')}</Link>
                <Link to="/how-it-works" style={{ color: theme.alpha.white(0.4), textDecoration: 'none', fontSize: 13 }}>{t('nav.howItWorks')}</Link>
                <Link to="/contact" style={{ color: theme.alpha.white(0.4), textDecoration: 'none', fontSize: 13 }}>{t('landing.contactLink')}</Link>
                <Link to="/login" style={{ color: theme.alpha.white(0.4), textDecoration: 'none', fontSize: 13 }}>{t('nav.signIn')}</Link>
              </div>
            </div>
            <div>
              <div style={{ color: theme.white, fontSize: 10, letterSpacing: '0.18em', marginBottom: 20 }}>{t('landing.footerSocial')}</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                <a href="https://instagram.com/glamgo.hair" target="_blank" rel="noreferrer" style={{ display: 'flex', alignItems: 'center', gap: 10, color: theme.alpha.white(0.4), textDecoration: 'none', fontSize: 13 }}>
                  <IgIcon /> @glamgo.hair
                </a>
                <a href="https://tiktok.com/@glamgo.hair" target="_blank" rel="noreferrer" style={{ display: 'flex', alignItems: 'center', gap: 10, color: theme.alpha.white(0.4), textDecoration: 'none', fontSize: 13 }}>
                  <TkIcon /> @glamgo.hair
                </a>
              </div>
            </div>
          </div>
          {/* The trader has to be identifiable from the site itself (Art. 5,
              E-Commerce Directive) — not only from inside the Terms, which is
              where the only mention of us used to live, and where it said
              nothing beyond "GlamGo … Cyprus". */}
          <div style={{ borderTop: `1px solid ${theme.alpha.white(0.05)}`, paddingTop: 24, marginBottom: 20 }}>
            <TraderIdentity style={{ fontSize: 11, lineHeight: 1.9, color: theme.alpha.white(0.32), margin: 0 }} />
          </div>
          <div style={{ borderTop: `1px solid ${theme.alpha.white(0.05)}`, paddingTop: 24, display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12, alignItems: 'center' }}>
            <span style={{ fontSize: 11 }}>{t('landing.copyright')}</span>
            <div style={{ display: 'flex', gap: 24 }}>
              <Link to="/privacy" style={{ color: theme.alpha.white(0.22), textDecoration: 'none', fontSize: 11 }}>{t('landing.privacyPolicy')}</Link>
              <Link to="/terms" style={{ color: theme.alpha.white(0.22), textDecoration: 'none', fontSize: 11 }}>{t('landing.termsOfService')}</Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}

function FaqItem({ q, a }) {
  const [open, setOpen] = React.useState(false);
  return (
    <div style={{ borderBottom: `1px solid ${theme.sand}`, padding: '22px 0' }}>
      <button onClick={() => setOpen(!open)} style={{ width: '100%', background: 'none', border: 'none', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontFamily: theme.font.display, fontSize: 20, color: theme.ink, textAlign: 'left', padding: 0 }}>
        <span>{q}</span>
        <ChevronDown size={18} style={{ transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s', color: theme.gold, flexShrink: 0 }} />
      </button>
      {open && <p style={{ color: theme.grey600, fontSize: 14, lineHeight: 1.75, margin: '16px 0 0', fontFamily: theme.font.body }}>{a}</p>}
    </div>
  );
}
